import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Smartphone, 
  Trophy, 
  Clock, 
  Users, 
  Zap, 
  Gift, 
  Shield, 
  Star,
  TrendingUp,
  GamepadIcon,
  UserPlus,
  Crown,
  ExternalLink
} from 'lucide-react';
import { featuredGames, recentWinners, gameStats, howItWorksSteps } from '../data/mockData';
import { toast } from 'sonner';
import WinnerAnnouncement from '../components/WinnerAnnouncement';

const BACKEND_URL = window.location.origin;

const Home = () => {
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [activeGame, setActiveGame] = useState(null);
  const [loadingGame, setLoadingGame] = useState(true);
  const [currentCompetition, setCurrentCompetition] = useState(null);
  const [topPrize, setTopPrize] = useState(null);

  // Fetch competition and prize data
  useEffect(() => {
    const fetchCompetitionData = async () => {
      try {
        // Fetch current competition
        const compResponse = await fetch(`${BACKEND_URL}/api/competition/current`);
        const compData = await compResponse.json();
        setCurrentCompetition(compData.competition);
        
        // Fetch top prize
        const prizeResponse = await fetch(`${BACKEND_URL}/api/admin/prizes`);
        const prizeData = await prizeResponse.json();
        const firstPrize = prizeData.prizes?.find(p => p.position === 1);
        setTopPrize(firstPrize);
        
        // Also fetch active game for fallback
        const gameResponse = await fetch(`${BACKEND_URL}/api/games/active`);
        const gameData = await gameResponse.json();
        const active = gameData.active_games?.find(g => g.is_active);
        setActiveGame(active);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingGame(false);
      }
    };
    fetchCompetitionData();
  }, []);

  useEffect(() => {
    // Check for session_id in URL fragment (from Emergent Auth)
    const handleOAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setIsProcessingAuth(true);
        
        // Extract session_id from URL fragment
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');
        
        if (sessionId) {
          try {
            // Call backend to create session
            const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: sessionId })
            });
            
            if (response.ok) {
              const data = await response.json();
              
              // Store session token and user data
              localStorage.setItem('fokatmain_token', data.session_token);
              localStorage.setItem('fokatmain_user', JSON.stringify(data.user));
              
              // Clean URL fragment
              window.history.replaceState({}, document.title, window.location.pathname);
              
              toast.success(`Welcome back, ${data.user.name}! 🎉`);
              
              // Reload to update header
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            } else {
              const error = await response.json();
              toast.error(error.detail || 'Authentication failed');
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          } catch (error) {
            console.error('OAuth callback error:', error);
            toast.error('Failed to complete sign-in');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
        
        setIsProcessingAuth(false);
      }
    };
    
    handleOAuthCallback();
  }, []);

  if (isProcessingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-white text-xl">Completing sign-in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Hero Section */}
      <section className="relative pt-20 lg:pt-32 pb-20 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/10 to-transparent"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-yellow-400/5 rounded-full blur-3xl animate-float"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="text-center lg:text-left animate-fade-in-up">
              <div className="flex justify-center lg:justify-start mb-6">
                <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 px-4 py-2 font-semibold animate-pulse-glow">
                  ⚡ Play • Win • Repeat
                </Badge>
              </div>
              
              <h1 className="font-heading text-hero text-white mb-6 leading-tight">
                Play Skill-Games.
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                  {' '}Win Gadgets.
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed">
                {currentCompetition ? (
                  <>Weekly Competition • {currentCompetition.game_name} • ₹10 = 3 Attempts</>
                ) : (
                  <>Weekly Skill Competitions • Saturday 9 PM • Win Real Gadgets</>
                )}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                {activeGame && !loadingGame ? (
                  <Button 
                    className="btn-primary px-8 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-all duration-300 flex items-center space-x-3 animate-pulse"
                    onClick={() => window.location.href = '/play-games'}
                  >
                    <Trophy className="w-5 h-5" />
                    <span>Play Now • Win Real Gadgets</span>
                    <Zap className="w-5 h-5" />
                  </Button>
                ) : (
                  <Button 
                    className="btn-primary px-8 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-all duration-300 flex items-center space-x-3"
                    onClick={() => window.location.href = '/play-games'}
                  >
                    <GamepadIcon className="w-5 h-5" />
                    <span>Explore Games</span>
                  </Button>
                )}
                <Button 
                  className="btn-secondary px-8 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-all duration-300"
                  onClick={() => window.location.href = '/login'}
                >
                  Sign Up Free
                </Button>
              </div>
              
              {/* Live Game Badge */}
              {activeGame && !loadingGame && (
                <div className="inline-flex items-center space-x-2 bg-green-500/20 border border-green-500/50 px-4 py-2 rounded-full mb-6 animate-bounce-slow">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-400 font-semibold">🔥 LIVE NOW: {activeGame.game_name}</span>
                </div>
              )}
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-6 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span>UPI/Paytm/Razorpay</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  <span>Winners Every Day</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span>50K+ Players</span>
                </div>
              </div>
            </div>
            
            {/* Hero Visual */}
            <div className="relative animate-slide-in-right">
              <div className="relative mx-auto max-w-sm lg:max-w-lg">
                {/* Phone Mockup */}
                <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-2 shadow-2xl animate-float">
                  <div className="bg-black rounded-2xl p-6 flex flex-col justify-between">
                    <div className="text-center">
                      <img 
                        src="/fokatmain_round_logo.jpeg" 
                        alt="App Logo" 
                        className={`h-20 w-20 mx-auto mb-4 logo-round border-3 ${
                          currentCompetition?.status === 'live' || currentCompetition?.status === 'registration_open'
                            ? 'border-green-400'
                            : 'border-yellow-400/50'
                        }`}
                      />
                      <div className="space-y-3">
                        {currentCompetition ? (
                          <>
                            <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl p-4 border border-yellow-400/30">
                              <div className="flex items-center justify-center gap-2 mb-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <div className="text-xs text-green-400 font-semibold">LIVE COMPETITION</div>
                              </div>
                              <div className="text-yellow-400 text-lg font-bold">{currentCompetition.game_name}</div>
                              <div className="text-gray-300 text-xs mt-1">
                                {new Date(currentCompetition.competition_starts_at).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })} at {new Date(currentCompetition.competition_starts_at).toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit' 
                                })}
                              </div>
                              <div className="text-gray-400 text-xs mt-1">₹10 = 3 attempts</div>
                            </div>
                            <div className="bg-gray-800/50 rounded-xl p-3">
                              <div className="text-gray-400 text-xs">
                                {currentCompetition.total_registered || 0} players registered
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bg-blue-900/30 rounded-xl p-3">
                              <div className="text-yellow-400 text-sm font-semibold">Weekly Competitions</div>
                              <div className="text-gray-300 text-xs">Every Saturday • ₹10 entry</div>
                            </div>
                            <div className="bg-purple-900/30 rounded-xl p-3">
                              <div className="text-yellow-400 text-sm font-semibold">Skill-Based Games</div>
                              <div className="text-gray-300 text-xs">Win Real Gadgets</div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-center mt-4">
                      <div className="text-xs text-gray-500 mb-2">
                        {currentCompetition?.status === 'live' || currentCompetition?.status === 'registration_open' 
                          ? 'This Week\'s Top Prize' 
                          : 'Weekly Top Prizes'}
                      </div>
                      <div className="text-yellow-400 font-bold text-lg">
                        {topPrize ? topPrize.prize_name : 'Real Gadgets!'}
                      </div>
                      {topPrize?.prize_description && (
                        <div className="text-gray-400 text-xs mt-1">{topPrize.prize_description}</div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 bg-yellow-400 text-black px-4 py-2 rounded-full font-bold text-sm animate-pulse-glow">
                  ₹10/Play
                </div>
                <div className="absolute -bottom-4 -left-4 bg-green-400 text-black px-4 py-2 rounded-full font-bold text-sm animate-bounce">
                  Skill-Based
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Winner Announcement Section */}
      <section className="py-12 bg-gradient-to-b from-transparent to-gray-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <WinnerAnnouncement />
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gradient-to-b from-transparent to-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 mb-4">
              Simple Process
            </Badge>
            <h2 className="font-heading text-section-title text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Get started in minutes and start winning amazing gadgets with your skills
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => {
              const IconComponent = {
                UserPlus,
                Gamepad2: GamepadIcon,
                Trophy,
                Gift
              }[step.icon];
              
              return (
                <Card key={step.step} className="gaming-card text-center p-6 animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <CardHeader className="pb-4">
                    <div className="mx-auto mb-4 p-4 bg-yellow-400/20 rounded-full w-16 h-16 flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-yellow-400" />
                    </div>
                    <div className="text-3xl font-bold text-yellow-400 mb-2">{step.step}</div>
                    <CardTitle className="text-xl text-white">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 mb-3">{step.description}</p>
                    <p className="text-sm text-gray-500">{step.detail}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Games */}
      <section className="py-20 bg-gradient-to-b from-gray-900/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mb-4">
                Live Games
              </Badge>
              <h2 className="font-heading text-section-title text-white mb-4">
                Featured Games
              </h2>
              <p className="text-xl text-gray-400">
                Quick skill-based games with amazing prizes
              </p>
            </div>
            <Button 
              className="btn-secondary hidden sm:flex"
              onClick={() => window.location.href = '/play-games'}
            >
              Play All Games
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredGames.map((game, index) => (
              <Card key={game.id} className="gaming-card overflow-hidden animate-fade-in-up" style={{ animationDelay: `${index * 0.15}s` }}>
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start mb-3">
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      {game.category}
                    </Badge>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">Entry Fee</div>
                      <div className="text-lg font-bold text-yellow-400">₹{game.entryFee}</div>
                    </div>
                  </div>
                  <CardTitle className="text-xl text-white">{game.title}</CardTitle>
                  <p className="text-gray-400">{game.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Duration:</span>
                      <span className="text-white font-semibold">{game.duration}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Players Today:</span>
                      <span className="text-green-400 font-semibold">{game.playersToday?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Competition Ends:</span>
                      <span className="text-red-400 font-semibold">{game.timeLeft}</span>
                    </div>
                    <Button 
                      className="w-full btn-primary mt-4 flex items-center justify-center gap-2"
                      onClick={() => window.location.href = '/play-games'}
                    >
                      <Trophy className="w-4 h-4" />
                      <span>Play Now • ₹10</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Winners */}
      <section className="py-20 bg-gradient-to-b from-transparent to-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 mb-4">
              Hall of Fame
            </Badge>
            <h2 className="font-heading text-section-title text-white mb-4">
              Recent Winners
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Real players winning real gadgets every day
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {recentWinners.slice(0, 6).map((winner, index) => (
              <Card key={winner.id} className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700 hover:border-yellow-400/50 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <Crown className="w-6 h-6 text-yellow-400" />
                    <div>
                      <div className="font-semibold text-white">{winner.name}</div>
                      <div className="text-sm text-gray-400">{winner.city}</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-lg font-semibold text-yellow-400">{winner.prize}</div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Game:</span>
                      <span className="text-white">{winner.game}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Score:</span>
                      <span className="text-green-400 font-semibold">{winner.score.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Won:</span>
                      <span className="text-white">{winner.date}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button className="btn-secondary px-8 py-3">
              View All Winners
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Win Amazing
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                {' '}Gadgets?
              </span>
            </h2>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed">
              Start playing skill-based games and win real gadgets and prizes!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                onClick={() => window.location.href = '/play-games'}
                className="btn-primary px-10 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-all duration-300 flex items-center space-x-3"
              >
                <Trophy className="w-5 h-5" />
                <span>Play Now & Win</span>
              </Button>
              <Button 
                onClick={() => window.location.href = '/login'}
                className="btn-secondary px-10 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-all duration-300"
              >
                Sign Up Free
              </Button>
            </div>
            
            <div className="flex justify-center space-x-8 text-sm text-gray-500">
              <span className="flex items-center space-x-2">
                <Star className="w-4 h-4" />
                <span>4.8/5 Rating</span>
              </span>
              <span className="flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span>100% Safe & Fair</span>
              </span>
              <span className="flex items-center space-x-2">
                <Zap className="w-4 h-4" />
                <span>Instant Payouts</span>
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;